import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { logger } from '@adornis/base/logging.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Observable } from 'rxjs';
import { SUPPORTED_LANGUAGES } from '../../i18n.js';
import { can } from '../authz/access.js';
import { getSubject } from '../authz/subject.js';
import { Conversation, UserMessage } from '../conversation.js';
import { Exam, type ExamState } from '../exam.js';
import { getPendingTaskDescs, isPendingTaskReady, type PendingTaskPriority } from './pending-tasks/pending-task.js';
import { finalizeLatestAssignmentProposal } from './update-assignment-proposal.js';
import { finalizeAgentTurn, runAgentStreamingLoop } from './utils/agent-loop.js';

interface CanvasContext {
  isOpen?: boolean;
  view?:
    | 'boundingbox'
    | 'correction'
    | 'answers'
    | 'msedit'
    | 'summary'
    | 'structureeditor'
    | 'submissioncheck'
    | 'demoselector'
    | 'legal'
    | 'none';
  selectedTaskNumber?: string;
  selectedStudentId?: string;
  selectedStudentName?: string;
  selectedTaskName?: string;
  selectedTaskExtractedText?: string;
  selectedTaskMasterSolution?: string;
  selectedTaskGradingCriteria?: string;
  selectedTaskGradingMaxPoints?: number;
  visibleTaskNumbers?: string[];
}

const CANVAS_VIEW_LABELS: Record<string, string | null> = {
  boundingbox: 'Struktur-/Bounding-Box-Bearbeitung',
  correction: 'Korrektur-Ansicht (Bewertung der Schülerantworten)',
  answers: 'Antworten-Ansicht (nur Sichtung der Schülerantworten, ohne Korrektur-Fokus)',
  msedit: 'Musterlösung/Bewertungsschema-Editor',
  summary: 'Zusammenfassung/Ergebnisübersicht',
  structureeditor: 'Struktur-Editor (Aufgabenbereiche)',
  submissioncheck: 'Schüleraufteilung/Abgaben-Check',
  demoselector: 'Auswahl der Beispielklausur',
  legal: 'Legal Navigator (Recht/Regulatorik)',
  none: null,
};

// --- HILFSFUNKTIONEN ---

const POINTS_REGEX = /(\d+(?:[.,]\d+)?)\s*(Punkte?|Pkt\.?)/gi;

const extractPointsMatches = (criteria: string) => {
  const matches: Array<{ start: number; end: number; value: number; unit: string }> = [];
  const regex = new RegExp(POINTS_REGEX.source, 'gi');
  let match: RegExpExecArray | null = regex.exec(criteria);

  while (match) {
    const value = Number((match[1] || '0').replace(',', '.'));
    if (Number.isFinite(value) && value >= 0) {
      const start = match.index;
      matches.push({
        start,
        end: start + match[0].length,
        value,
        unit: match[2] || 'Punkte',
      });
    }
    match = regex.exec(criteria);
  }
  return matches;
};

const formatScaledPoints = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.00$/, '').replace(/0$/, '').replace('.', ',');
};

export const scaleCriteriaToTargetMax = (
  criteria: string,
  previousMaxPoints: number,
  targetMaxPoints: number,
): string => {
  const matches = extractPointsMatches(criteria);
  if (!matches.length || previousMaxPoints <= 0) return criteria;

  const factor = targetMaxPoints / previousMaxPoints;
  const targetHalfPoints = Math.max(0, Math.round(targetMaxPoints * 2));
  const rawHalfPoints = matches.map(item => item.value * factor * 2);
  const roundedHalfPoints = rawHalfPoints.map(value => Math.max(0, Math.round(value)));

  let diff = targetHalfPoints - roundedHalfPoints.reduce((sum, value) => sum + value, 0);
  if (diff !== 0) {
    const ranking = rawHalfPoints
      .map((rawValue, index) => ({ index, delta: rawValue - (roundedHalfPoints[index] ?? 0) }))
      .sort((a, b) => (diff > 0 ? b.delta - a.delta : a.delta - b.delta));

    let guard = 0;
    while (diff !== 0 && guard < 10000) {
      let changed = false;
      for (const entry of ranking) {
        if (diff === 0) break;
        const current = roundedHalfPoints[entry.index] ?? 0;
        if (diff > 0) {
          roundedHalfPoints[entry.index] = current + 1;
          diff -= 1;
          changed = true;
        } else if (current > 0) {
          roundedHalfPoints[entry.index] = current - 1;
          diff += 1;
          changed = true;
        }
      }
      if (!changed) break;
      guard += 1;
    }
  }

  let rebuilt = '';
  let cursor = 0;
  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    if (!match) continue;
    rebuilt += criteria.slice(cursor, match.start);
    rebuilt += `${formatScaledPoints((roundedHalfPoints[index] ?? 0) / 2)} ${match.unit}`;
    cursor = match.end;
  }
  rebuilt += criteria.slice(cursor);
  return rebuilt;
};

/**
 * Soft guide for merging a workflow-task notification into an in-progress answer. Encourages a
 * natural, optional extension of the current reply rather than a forced restart or a separate message.
 *
 * The concrete task description is embedded here rather than left to the refreshed exam context — the
 * same reason `buildWorkflowNotification` does it for the standalone pass. Pointing at the context and
 * hoping the agent finds the task there produced continuations that invented their own "news" from
 * whatever else looked new in the exam state (an unconfirmed split, an MS that was suddenly present).
 */
const buildWorkflowMergeGuide = (workflowTasks: Array<Record<string, unknown>>): string => {
  const descs = getPendingTaskDescs(workflowTasks);
  return (
    'SYSTEM: Während du geantwortet hast, ist die unten aufgeführte Hintergrundaufgabe abgeschlossen worden. ' +
    'Ergänze deine bisherige Antwort beiläufig und natürlich um genau diese Neuigkeit — fang nicht von vorne an, ' +
    'wiederhole dich nicht und erfinde keine anderen Neuigkeiten. Knüpfe nahtlos an deinen letzten Satz an und ' +
    'schreibe ausschließlich die Fortsetzung, nicht die gesamte Antwort erneut.' +
    (descs ? `\n\n${descs}` : '')
  );
};

// --- MAIN SUBSCRIPTION ---
export const generateAnswer = registerSubscription({
  operationName: 'generateAnswer',
  type: () => Conversation,
  params: [
    { name: 'conversationId', type: () => String },
    { name: 'question', type: () => String },
    { name: 'activeCanvasContext', type: () => String },
    { name: 'editMode', type: () => Boolean },
    { name: 'guestUserID', type: () => String },
    { name: 'userLanguage', type: () => String },
  ],
  options: {
    // Has side effects (writes messages, runs tools). On WS reconnect the
    // streamConversationByIdSubscription keeps delivering the in-flight result,
    // so the client must NOT re-issue this op or the assistant runs again.
    requestHandlerMeta: { transport: { dontRestart: true } },
  },
  resolve:
    (
      conversationId: string,
      question: string,
      activeCanvasContext: string,
      editMode = false,
      guestUserID?: string,
      userLanguage = 'de',
    ) =>
    (gqlFields: BaseQLSelectionSet<Conversation>) =>
      new Observable<Conversation>(subscriber => {
        const run = async () => {
          // The session's identity only — the `guestUserID` parameter is ignored (see resolveOwnerID). Refusals
          // RETURN instead of throwing: the catch below resets `thinking` on `conversationId`, which must never
          // happen for a conversation the caller does not own.
          const subject = await getSubject();
          const currentUserId = subject.userID ?? subject.guestID;
          if (!currentUserId) {
            subscriber.error(new AccessDeniedError('Access denied: no session identity', { reason: 'no identity' }));
            return;
          }

          // A Student profile existing for this userID is what makes them a student (see
          // db/student.ts / db/teacher.ts — same "whichever profile exists" resolution the client
          // uses in useCurrentUserProfile.ts). No Student profile (incl. guests) defaults to teacher,
          // matching every account that existed before the student role was introduced.
          const currentUserRole: 'teacher' | 'student' = subject.studentId ? 'student' : 'teacher';

          const conversationCollection = await getCollection(Conversation);
          // Raw read: a foreign conversation must be FOUND (and refused below), not filtered out and then
          // "re-created" — that path ended in the catch below, resetting the foreign conversation's state.
          const storedConversation = await (await getRawCollection(Conversation._collectionName)).findOne({
            _id: conversationId,
          } as any);
          if (storedConversation && !(await can(Conversation, 'update', storedConversation))) {
            subscriber.error(new AccessDeniedError('Access denied', { reason: 'not the conversation owner' }));
            return;
          }
          let existingConversation = await conversationCollection.findOne({ _id: conversationId });

          if (!existingConversation) {
            const newConversation = new Conversation({ _id: conversationId, userID: currentUserId }).toObject();
            try {
              await conversationCollection.updateOne(
                { _id: conversationId },
                { $setOnInsert: newConversation },
                { upsert: true },
              );
            } catch (e: any) {
              if (e?.code !== 11000) throw e;
              // E11000: concurrent request created the document first — fall through to re-fetch
            }
            existingConversation = await conversationCollection.findOne({ _id: conversationId });
            if (!existingConversation) {
              throw new Error('Failed to create conversation');
            }
          }
          logger.info(
            { conversationId, userID: existingConversation.userID, currentUserId, context: 'GA' },
            'Existing conversation found',
          );
          const generateAnswerStartTime = Date.now();
          logger.info(
            { conversationId, userId: currentUserId, examId: existingConversation.examId ?? 'none', context: 'GA' },
            'Started generating answer',
          );

          existingConversation.thinking = true;

          let currentExam: any = null;
          let currentExamState = {
            taskExtractionProgress: { status: '' },
            uploadProgress: { totalFiles: 0 },
          } as unknown as ExamState;
          let parsedCanvasContext: CanvasContext = {};
          try {
            parsedCanvasContext = activeCanvasContext ? (JSON.parse(activeCanvasContext) as CanvasContext) : {};
          } catch {
            parsedCanvasContext = {};
          }

          // isOpen gates the label: a stale/previous `view` value must never be reported as "open" once
          // the canvas has actually been closed — the string below is this message's ONLY source of
          // truth for what's currently visible, overriding anything implied by earlier chat history.
          const openCanvasLabel = parsedCanvasContext.isOpen
            ? (CANVAS_VIEW_LABELS[parsedCanvasContext.view ?? 'none'] ?? null)
            : null;
          const isMsGsCanvasOpen = parsedCanvasContext.isOpen && parsedCanvasContext.view === 'msedit';
          const isGradingCanvasOpen =
            parsedCanvasContext.isOpen &&
            (parsedCanvasContext.view === 'correction' || parsedCanvasContext.view === 'answers');

          const interactionContextString = `
              KONTEXT ZUR AKTUELLEN UI (Stand DIESER Nachricht — nutze für Fragen zum offenen Canvas AUSSCHLIESSLICH diesen Block, nicht frühere Aussagen im Gesprächsverlauf, da sich der Canvas seitdem geändert haben kann):
              - Aktuell geöffneter Canvas-Bereich: ${openCanvasLabel || 'kein Canvas geöffnet'}
              - Dort aktuell sichtbare Aufgabe: ${parsedCanvasContext.selectedTaskNumber || 'keine erkennbar'}
              - Ausgewählter Schüler: ${parsedCanvasContext.selectedStudentId || 'Nicht angegeben'}${
                parsedCanvasContext.selectedStudentName ? ` (${parsedCanvasContext.selectedStudentName})` : ''
              }
              - WICHTIG: Diese UI-Angaben sind Kontext für Erklärungen/Rückfragen und dürfen als Standard-Ziel für set_task_score dienen. Für Musterlösung/Bewertungsschema (MS/GS) gilt das NICHT — dort niemals Canvas-Auswahl voraussetzen, die Aufgabennummer muss immer explizit vom Nutzer genannt werden.
              - WICHTIG ZUR BEGRIFFSKLÄRUNG "PUNKTE": Ist der MS/GS-Editor offen (${isMsGsCanvasOpen ? 'JA, aktuell offen' : 'aktuell NICHT offen'}), meint "X Punkte für Aufgabe Y" i.d.R. die MAXIMALPUNKTZAHL im Bewertungsschema → nutze refine_grading_scheme, NIEMALS set_task_score (dort gibt es keinen einzelnen Schüler, also frage auch nie nach einem Schülernamen). Ist stattdessen der Korrektur-/Antworten-Canvas offen (${isGradingCanvasOpen ? 'JA, aktuell offen' : 'aktuell NICHT offen'}) oder nennt der Nutzer einen Schüler, meint "X Punkte für Aufgabe Y" die Bewertung dieser Abgabe → nutze set_task_score. Ist beides unklar (kein passender Canvas offen, kein Schüler genannt), frage kurz nach, ob die Punktzahl eines Schülers oder die Maximalpunktzahl im Bewertungsschema gemeint ist.
              - Bei "Aufgabentext falsch" verwende update_open_task_content mit target="task_text".
            `;

          if (existingConversation.examId) {
            const examCollection = await getCollection(Exam as any);
            currentExam = await examCollection.findOne({ _id: existingConversation.examId as any });
            currentExamState = ((currentExam as any)?.examState ?? currentExamState) as ExamState;
          }

          let newMessages: any[];
          if (editMode) {
            const existingMessages = existingConversation.messages ?? [];
            let lastUserMessageIndex = -1;
            for (let i = existingMessages.length - 1; i >= 0; i--) {
              const msg = existingMessages[i];
              if (msg && (msg._class === 'UserMessage' || msg._class === 'user')) {
                lastUserMessageIndex = i;
                break;
              }
            }
            if (lastUserMessageIndex === -1) {
              subscriber.error(new Error('Keine Message zum Bearbeiten gefunden'));
              return;
            }
            const updatedMessages: any[] = existingMessages.slice(0, lastUserMessageIndex);
            updatedMessages.push(new UserMessage({ content: question }).toObject());
            newMessages = updatedMessages;
          } else {
            newMessages = [...(existingConversation.messages ?? []), new UserMessage({ content: question }).toObject()];
          }

          // Safety net: ensure pending assignment proposal messages are finalized before the next model answer.
          await finalizeLatestAssignmentProposal(conversationId, newMessages);

          const normalizedUserLanguage = String(userLanguage || '').trim();
          const mappedUserLanguage =
            SUPPORTED_LANGUAGES.find(entry => entry.value === normalizedUserLanguage)?.label || normalizedUserLanguage;
          const conversationLanguage = SUPPORTED_LANGUAGES.some(entry => entry.label === mappedUserLanguage)
            ? mappedUserLanguage
            : String((existingConversation as any)?.language || '').trim() || 'Deutsch';

          await (
            await getRawCollection(Conversation._collectionName)
          ).updateOne(
            { _id: conversationId as any },
            {
              $set: { messages: newMessages, thinking: true, language: conversationLanguage, lastUpdated: new Date() },
            },
          );
          subscriber.next(
            new Conversation({
              ...existingConversation,
              messages: newMessages,
              thinking: false,
              language: conversationLanguage,
              lastUpdated: new Date(),
            }),
          );

          // Run the shared streaming agentic loop (it builds the system prompt + payload + tools
          // internally), emitting previews straight to this subscriber.
          const loopResult = await runAgentStreamingLoop({
            conversationId,
            examId: existingConversation.examId,
            exam: currentExam,
            examState: currentExamState,
            language: conversationLanguage,
            role: currentUserRole,
            messages: newMessages,
            systemContextSuffix: interactionContextString,
            canvasContext: parsedCanvasContext,
            existingConversation: existingConversation as unknown as Conversation,
            emit: conv => subscriber.next(conv),
          });
          const suppressAssistantFollowUp = loopResult.suppressAssistantFollowUp;
          // These may be extended below by a mid-answer workflow merge, so they are mutable.
          let finalAssistantContent = loopResult.finalAssistantContent;
          let latestMessages = loopResult.latestMessages;
          let canvasToOpen = loopResult.canvasToOpen;
          let pendingDownload = loopResult.pendingDownload;
          let pendingSettingsChange = loopResult.pendingSettingsChange;
          let startStructureExtraction = loopResult.startStructureExtraction;

          // If a workflow-priority task landed WHILE the agent was answering, merge it into THIS same
          // answer instead of posting a separate follow-up — the agent weaves the news in naturally
          // ("Übrigens, ich habe gerade erfahren, dass …"). Guided, not forced. (When the agent is idle
          // the standing workflow subscription / pushWorkflowTask fallback handles it as its own reply.)
          //
          // Readiness is filtered exactly as in `runWorkflowAgentPass`: a SetupStep task is PUSHED as soon
          // as its first step finishes and only released (isReadyForAgent) once nothing is running any more.
          // Selecting on priority alone announced such a task mid-run — while the split confirmation was
          // still open — and then $pulled it, which destroyed the results it was still accumulating and cost
          // the coalesced single announcement it exists for.
          if (existingConversation.examId && !suppressAssistantFollowUp && finalAssistantContent.trim()) {
            try {
              const examCollection = await getCollection(Exam as any);
              const freshExam = await examCollection.findOne({ _id: existingConversation.examId as any });
              const freshTasks: Array<Record<string, unknown>> = Array.isArray(
                (freshExam as any)?.examState?.pendingTasks,
              )
                ? (freshExam as any).examState.pendingTasks
                : [];
              const workflowTasks = freshTasks.filter(
                t => (t?.priority as PendingTaskPriority) === 'workflow' && isPendingTaskReady(t),
              );

              if (workflowTasks.length) {
                const workflowClasses = [...new Set(workflowTasks.map(t => String(t?._class ?? '')).filter(Boolean))];
                logger.info(
                  { context: 'GA', workflowClasses },
                  'Workflow task arrived mid-answer — merging into the current reply',
                );
                const freshExamState = ((freshExam as any)?.examState ?? currentExamState) as ExamState;

                // Continue the SAME message: history + the answer so far + the task itself plus a soft guide
                // to extend the reply. The fresh exam is passed so the surrounding context matches the news.
                const continuation = await runAgentStreamingLoop({
                  conversationId,
                  examId: existingConversation.examId,
                  exam: freshExam,
                  examState: freshExamState,
                  language: conversationLanguage,
                  role: currentUserRole,
                  messages: latestMessages,
                  systemContextSuffix: interactionContextString,
                  canvasContext: parsedCanvasContext,
                  trailingMessages: [
                    { role: 'assistant', content: finalAssistantContent },
                    { role: 'system', content: buildWorkflowMergeGuide(workflowTasks) },
                  ],
                  contentPrefix: `${finalAssistantContent}\n\n`,
                  existingConversation: existingConversation as unknown as Conversation,
                  emit: conv => subscriber.next(conv),
                });

                if (continuation.finalAssistantContent.trim()) {
                  finalAssistantContent = continuation.finalAssistantContent;
                }
                latestMessages = continuation.latestMessages;
                if (continuation.canvasToOpen) canvasToOpen = continuation.canvasToOpen;
                if (continuation.pendingDownload) pendingDownload = continuation.pendingDownload;
                if (continuation.pendingSettingsChange) pendingSettingsChange = continuation.pendingSettingsChange;
                startStructureExtraction = startStructureExtraction || continuation.startStructureExtraction;

                // Consume the merged tasks so the post-turn trigger doesn't re-post them standalone.
                for (const cls of workflowClasses) {
                  await examCollection.updateOne({ _id: existingConversation.examId } as any, {
                    $pull: { 'examState.pendingTasks': { _class: cls } } as any,
                  });
                }
              }
            } catch (mergeErr) {
              logger.error({ err: mergeErr, context: 'GA' }, 'Mid-answer workflow merge failed');
            }
          }

          // Finalize: persist the (possibly workflow-merged) reply and emit it. A workflow task that
          // arrived mid-answer was already merged above; tasks that arrive while idle are handled by
          // the standing workflow subscription (live) or pushWorkflowTask's fallback (final-only) —
          // no explicit post-turn trigger needed.
          await finalizeAgentTurn({
            conversationId,
            existingConversation: existingConversation as unknown as Conversation,
            examId: existingConversation.examId,
            language: conversationLanguage,
            loopResult: {
              finalAssistantContent,
              latestMessages,
              canvasToOpen,
              pendingDownload,
              pendingSettingsChange,
              startStructureExtraction,
              suppressAssistantFollowUp,
            },
            thinking: false,
            emit: conv => subscriber.next(conv),
          });

          logger.info(
            {
              conversationId,
              examId: existingConversation.examId ?? 'none',
              durationMs: Date.now() - generateAnswerStartTime,
              context: 'GA',
            },
            'Completed generating answer',
          );

          subscriber.complete();
        };

        void run().catch(async err => {
          logger.error({ err, conversationId, context: 'GA' }, 'generateAnswer failed');
          try {
            await (
              await getRawCollection(Conversation._collectionName)
            ).updateOne({ _id: conversationId } as any, { $set: { thinking: false } });
          } catch (resetErr) {
            logger.error({ err: resetErr, conversationId, context: 'GA' }, 'Failed to reset thinking state after error');
          }
          subscriber.error(err);
        });
      }),
});
